import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Lock, Plus, ShieldCheck } from "lucide-react";
import {
  Badge, Button, Card, EmptyState, ErrorText, Input, Modal,
  PageHeader, Skeleton, useToast,
} from "../components/ui";
import { useBranding } from "../context/BrandingContext";
import { ApiError, apiFetch } from "../lib/api";

interface PermissionItem {
  key: string;
  module: string;
  action: string;
  labelAr: string;
  labelEn: string;
  sensitive: boolean;
}

interface RoleItem {
  id: string;
  key: string;
  nameAr: string;
  nameEn: string;
  descriptionAr: string | null;
  descriptionEn: string | null;
  isSystem: boolean;
  isOwnerRole: boolean;
  userCount: number;
  permissions: string[];
}

/**
 * Roles and permissions.
 *
 * The owner role is shown — hiding it would be more confusing than useful,
 * since it visibly holds every permission and explains why — but every
 * mutating control is absent for it. That mirrors the server exactly: PATCH
 * and DELETE on the owner role are refused independently of anything this
 * screen does or doesn't render.
 */
export function RolesPage() {
  const { language } = useBranding();
  const { showToast } = useToast();
  const ar = language === "ar";

  const [roles, setRoles] = useState<RoleItem[] | null>(null);
  const [permissions, setPermissions] = useState<PermissionItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [editing, setEditing] = useState<RoleItem | "new" | null>(null);
  const [form, setForm] = useState({ key: "", nameAr: "", nameEn: "", descriptionAr: "", descriptionEn: "", permissions: [] as string[] });
  const [saveBusy, setSaveBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<RoleItem | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [r, p] = await Promise.all([
        apiFetch<{ items: RoleItem[] }>("/roles"),
        apiFetch<{ items: PermissionItem[] }>("/roles/permissions"),
      ]);
      setRoles(r.items);
      setPermissions(p.items);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : ar ? "تعذّر تحميل الأدوار." : "Couldn't load roles.");
    }
  }, [ar]);

  useEffect(() => { void load(); }, [load]);

  const byModule = useMemo(() => {
    const map = new Map<string, PermissionItem[]>();
    for (const p of permissions) {
      const list = map.get(p.module) ?? [];
      list.push(p);
      map.set(p.module, list);
    }
    return map;
  }, [permissions]);

  const openNew = () => {
    setForm({ key: "", nameAr: "", nameEn: "", descriptionAr: "", descriptionEn: "", permissions: [] });
    setSaveError(null);
    setEditing("new");
  };

  const openEdit = (role: RoleItem) => {
    setForm({
      key: role.key, nameAr: role.nameAr, nameEn: role.nameEn,
      descriptionAr: role.descriptionAr ?? "", descriptionEn: role.descriptionEn ?? "",
      permissions: [...role.permissions],
    });
    setSaveError(null);
    setEditing(role);
  };

  const save = async () => {
    setSaveError(null);
    setSaveBusy(true);
    try {
      if (editing === "new") {
        await apiFetch("/roles", {
          method: "POST",
          body: {
            key: form.key.trim(), nameAr: form.nameAr.trim(), nameEn: form.nameEn.trim(),
            descriptionAr: form.descriptionAr.trim() || null, descriptionEn: form.descriptionEn.trim() || null,
            permissions: form.permissions,
          },
        });
        showToast("success", ar ? "أُنشئ الدور." : "Role created.");
      } else if (editing) {
        await apiFetch(`/roles/${editing.id}`, {
          method: "PATCH",
          body: {
            nameAr: form.nameAr.trim(), nameEn: form.nameEn.trim(),
            descriptionAr: form.descriptionAr.trim() || null, descriptionEn: form.descriptionEn.trim() || null,
            permissions: form.permissions,
          },
        });
        showToast("success", ar ? "حُفظ الدور." : "Role saved.");
      }
      setEditing(null);
      await load();
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : ar ? "تعذّر الحفظ." : "Couldn't save.");
    } finally {
      setSaveBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteError(null);
    setDeleteBusy(true);
    try {
      await apiFetch(`/roles/${deleteTarget.id}`, { method: "DELETE" });
      showToast("success", ar ? "حُذف الدور." : "Role deleted.");
      setDeleteTarget(null);
      await load();
    } catch (err) {
      setDeleteError(err instanceof ApiError ? err.message : ar ? "تعذّر الحذف." : "Couldn't delete.");
    } finally {
      setDeleteBusy(false);
    }
  };

  if (error) {
    return (
      <div className="space-y-4">
        <PageHeader title={ar ? "الأدوار" : "Roles"} />
        <Card>
          <EmptyState icon={<AlertTriangle className="size-6" />} title={ar ? "تعذّر التحميل" : "Couldn't load"} description={error}
            action={<Button size="sm" onClick={load}>{ar ? "إعادة المحاولة" : "Retry"}</Button>} />
        </Card>
      </div>
    );
  }

  if (!roles) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-7 w-40" />
        <div className="grid gap-4 sm:grid-cols-2">
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title={ar ? "الأدوار" : "Roles"}
        subtitle={ar ? "من يملك صلاحية فعل ماذا." : "Who can do what."}
        trailing={<Button icon={<Plus className="size-4" />} onClick={openNew}>{ar ? "دور جديد" : "New role"}</Button>}
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {roles.map((role) => (
          <Card key={role.id}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="flex items-center gap-1.5 font-semibold text-ink-900">
                  {ar ? role.nameAr : role.nameEn}
                  {role.isOwnerRole && <ShieldCheck className="size-4 text-accent-600" />}
                </h3>
                {(role.descriptionAr || role.descriptionEn) && (
                  <p className="mt-0.5 text-xs text-muted-foreground">{ar ? role.descriptionAr : role.descriptionEn}</p>
                )}
              </div>
              {role.isSystem && <Badge tone="neutral">{ar ? "نظام" : "System"}</Badge>}
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              {role.permissions.length} {ar ? "صلاحية" : "permissions"} · {role.userCount} {ar ? "مستخدم" : "users"}
            </p>
            <div className="mt-3 flex gap-2">
              {role.isOwnerRole ? (
                <span className="flex items-center gap-1 text-xs text-muted-subtle">
                  <Lock className="size-3" /> {ar ? "صلاحيات المالك ثابتة" : "The owner's permissions are fixed"}
                </span>
              ) : (
                <>
                  <Button size="sm" variant="outline" onClick={() => openEdit(role)}>{ar ? "تعديل" : "Edit"}</Button>
                  {!role.isSystem && (
                    <Button size="sm" variant="ghost" onClick={() => { setDeleteTarget(role); setDeleteError(null); }}>
                      {ar ? "حذف" : "Delete"}
                    </Button>
                  )}
                </>
              )}
            </div>
          </Card>
        ))}
      </div>

      {/* ── Create / edit ───────────────────────────────────────────────── */}
      <Modal
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing === "new" ? (ar ? "دور جديد" : "New role") : (ar ? "تعديل الدور" : "Edit role")}
        closeLabel={ar ? "إغلاق" : "Close"}
      >
        <div className="max-h-[70vh] space-y-3 overflow-y-auto">
          {editing === "new" && (
            <Input label={ar ? "المعرّف" : "Key"} value={form.key} onChange={(e) => setForm((f) => ({ ...f, key: e.target.value }))}
              helperText={ar ? "أحرف لاتينية صغيرة وشرطات سفلية فقط." : "Lowercase letters and underscores only."} required />
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            <Input label={ar ? "الاسم (عربي)" : "Name (Arabic)"} value={form.nameAr} onChange={(e) => setForm((f) => ({ ...f, nameAr: e.target.value }))} required />
            <Input label={ar ? "الاسم (إنجليزي)" : "Name (English)"} value={form.nameEn} onChange={(e) => setForm((f) => ({ ...f, nameEn: e.target.value }))} required />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Input label={ar ? "الوصف (عربي)" : "Description (Arabic)"} value={form.descriptionAr} onChange={(e) => setForm((f) => ({ ...f, descriptionAr: e.target.value }))} />
            <Input label={ar ? "الوصف (إنجليزي)" : "Description (English)"} value={form.descriptionEn} onChange={(e) => setForm((f) => ({ ...f, descriptionEn: e.target.value }))} />
          </div>

          <div>
            <p className="mb-2 text-xs font-medium text-ink-700">{ar ? "الصلاحيات" : "Permissions"}</p>
            <div className="space-y-3">
              {[...byModule.entries()].map(([module, perms]) => (
                <div key={module} className="rounded-lg border border-line p-3">
                  <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-subtle">{module}</p>
                  <div className="space-y-1">
                    {perms.map((p) => (
                      <label key={p.key} className="flex cursor-pointer items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={form.permissions.includes(p.key)}
                          onChange={(e) => setForm((f) => ({
                            ...f,
                            permissions: e.target.checked ? [...f.permissions, p.key] : f.permissions.filter((k) => k !== p.key),
                          }))}
                          className="size-4 rounded border-line-strong text-accent-500"
                        />
                        <span>{ar ? p.labelAr : p.labelEn}</span>
                        {p.sensitive && <Badge tone="warning">{ar ? "حساسة" : "Sensitive"}</Badge>}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {saveError && <ErrorText>{saveError}</ErrorText>}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setEditing(null)}>{ar ? "إلغاء" : "Cancel"}</Button>
          <Button loading={saveBusy} disabled={!form.nameAr.trim() || !form.nameEn.trim() || (editing === "new" && !form.key.trim())} onClick={save}>
            {ar ? "حفظ" : "Save"}
          </Button>
        </div>
      </Modal>

      {/* ── Delete ──────────────────────────────────────────────────────── */}
      <Modal open={!!deleteTarget} onClose={() => setDeleteTarget(null)} title={ar ? "حذف الدور" : "Delete role"} closeLabel={ar ? "إغلاق" : "Close"}>
        {deleteTarget && (
          <>
            <p className="text-sm text-ink-700">
              {ar
                ? `سيُحذف الدور «${deleteTarget.nameAr}». لا يمكن حذف دور مُسنَد إلى مستخدمين.`
                : `This deletes "${deleteTarget.nameEn}". A role assigned to any user cannot be deleted.`}
            </p>
            {deleteError && <div className="mt-3"><ErrorText>{deleteError}</ErrorText></div>}
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setDeleteTarget(null)}>{ar ? "تراجع" : "Keep it"}</Button>
              <Button variant="danger" loading={deleteBusy} onClick={confirmDelete}>{ar ? "حذف" : "Delete"}</Button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}
